import { logDev, errorDev } from '@/utils/env'
import appsignal from '@/appsignal'
import type { ApiResponse, ApiRequestOptions, ApiSuccessResponse } from '@/types'
import { TMC_CLIENT_PATIENT } from '@libtmc/tmcClient'

interface ApiErrorWithStatus extends Error {
  status?: number
  data?: unknown
}

export function backendHostForPage(configuredHost: string | undefined, pageHostname: string): string | undefined {
  if (pageHostname === 'app.migraine.org') return 'api.migraine.org'
  if (pageHostname === 'staging.app.migraine.org') return 'staging.api.migraine.org'
  return configuredHost
}

export function domainHandoffUrl(pageHostname: string, next: string): string | undefined {
  const legacyApiHosts: Record<string, string> = {
    'patient.themigraineclinic.org': 'api.themigraineclinic.org',
    'staging.patient.themigraineclinic.org': 'staging.api.themigraineclinic.org',
  }
  const apiHost = legacyApiHosts[pageHostname]
  if (!apiHost) return undefined
  return `https://${apiHost}/auth/domainHandoff?portal=patient&next=${encodeURIComponent(next)}`
}

function getBaseUrl(): string {
  const ssl = String(window._env_?.BACKEND_SSL) === '1' ? 'https' : 'http'
  // Keep API requests same-site so SameSite=Strict auth cookies work on both
  // the current and new domain families. BACKEND_HOST remains the fallback for
  // local development and the themigraineclinic.org hosts.
  const host = backendHostForPage(window._env_?.BACKEND_HOST, window.location.hostname)
  const port = window._env_?.BACKEND_PORT

  if (!host) {
    throw new Error('BACKEND_HOST ontbreekt: configureer window._env_.BACKEND_HOST')
  }

  return `${ssl}://${host}:${port}`
}

export async function apiFetch(endpoint: string, options: ApiRequestOptions = {}): Promise<Response> {
  const url = `${getBaseUrl()}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`

  // `credentials: 'include'` sends the HttpOnly auth cookie the backend set on
  // login. Auth no longer rides in an Authorization header (XSS protection).
  // `X-Tmc-Client` tells the backend which cookie to use so a patient session
  // does not shadow a staff session that coexists in the same browser.
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'X-Tmc-Client': TMC_CLIENT_PATIENT, ...(options.headers ?? {}) },
  })
}

function extractMessage(data: unknown): string | null {
  if (data == null) return null
  if (typeof data === 'string') return data
  if (typeof data === 'object') return (data as Record<string, unknown>).error as string || (data as Record<string, unknown>).message as string || null
  try {
    return String(data)
  } catch {
    return null
  }
}

export async function parseResponse<T>(response: Response): Promise<ApiResponse<T | null>> {
  const status = response.status
  const ct = response.headers.get('content-type') || ''
  let data: T | null = null

  try {
    data = ct.includes('application/json') ? await response.json() : await response.text()
  } catch (e) {
    appsignal?.sendError(e instanceof Error ? e : new Error('Response parse failure'), (span) => {
      span.setTags({ type: 'response-parse-error', status: String(status) })
      span.setParams({ status, contentType: ct, url: response.url })
      return span
    })
    data = null
  }

  logDev('apiClient parseResponse status:', status)
  logDev('apiClient parseResponse data:', data)

  return { ok: response.ok, status, data }
}

export async function handleApiCall<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<ApiSuccessResponse<T | null>> {
  try {
    logDev('apiClient Fetch options:', endpoint, options)

    const res = await apiFetch(endpoint, options)
    const { ok, status, data } = await parseResponse<T>(res)

    if (!ok) {
      const message = extractMessage(data) || `Request failed with status ${status}`
      const err: ApiErrorWithStatus = new Error(message)
      err.status = status
      err.data = data
      throw err
    }

    return { status, data }
  } catch (e) {
    errorDev('apiClient error:', e)

    const err: ApiErrorWithStatus = e instanceof Error ? e : new Error((e as ApiErrorWithStatus)?.message || 'Network error')
    if (e && (e as ApiErrorWithStatus).status) {
      err.status = (e as ApiErrorWithStatus).status
      err.data = (e as ApiErrorWithStatus).data
    }
    throw err
  }
}
